import React from 'react';

/**
 * MappingFlow — the signature rail of the Configuration pages.
 *
 * Reads left→right as the data flows: Source → Maps to → Lands in. Stations
 * are joined by hairline connectors with a chevron, so the mapping DIRECTION
 * is visible instead of being implied by column order. A `warn` station is a
 * button (click filters the table to what needs attention). Below the rail an
 * optional coverage meter: "190 / 191 mapped".
 *
 * props
 *   stations  [{ label, value, sub?, tone?: 'neutral'|'ok'|'warn'|'muted', onClick?, mono?: bool (default true) }]
 *   coverage  { mapped, total, label? }   label defaults to 'mapped'
 *   onUnmappedClick  fallback click handler for warn stations without their own onClick
 */
export default function MappingFlow({ stations = [], coverage, onUnmappedClick }) {
  if (!stations.length && !coverage) return null;
  const pct = coverage && coverage.total > 0 ? Math.min(100, Math.round((coverage.mapped / coverage.total) * 100)) : 0;
  const full = coverage && coverage.total > 0 && coverage.mapped >= coverage.total;
  return (
    <section className="cp-flow" aria-label="Mapping flow">
      {stations.length ? (
        <div className="cp-flow-rail">
          {stations.map((s, i) => {
            const tone = s.tone || 'neutral';
            const click = s.onClick || (tone === 'warn' ? onUnmappedClick : null);
            const cls = `cp-station cp-tone-${tone}`;
            const inner = (
              <>
                <div className="cp-station-eyebrow">{s.label}</div>
                <div className={`cp-station-value${s.mono === false ? ' cp-sans' : ''}`} title={typeof s.value === 'string' ? s.value : undefined}>{s.value}</div>
                {s.sub ? <div className="cp-station-sub" title={typeof s.sub === 'string' ? s.sub : undefined}>{s.sub}</div> : null}
              </>
            );
            return click ? (
              <button key={i} type="button" className={cls} onClick={click} title="Show these rows">
                {inner}
              </button>
            ) : (
              <div key={i} className={cls}>{inner}</div>
            );
          })}
        </div>
      ) : null}
      {coverage && coverage.total > 0 ? (
        <div className="cp-coverage" role="progressbar" aria-valuemin={0} aria-valuemax={coverage.total} aria-valuenow={coverage.mapped}>
          <div className="cp-coverage-track">
            <div className={`cp-coverage-fill${full ? ' cp-full' : ''}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="cp-coverage-label">
            <strong>{coverage.mapped.toLocaleString()}</strong> / {coverage.total.toLocaleString()} {coverage.label || 'mapped'}
          </div>
        </div>
      ) : null}
    </section>
  );
}
